import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from 'components/AppIcon';
import { validatePasswordStrength, getStrengthMeta, isCommonPassword } from '../../utils/validation';

const PasswordStrengthMeter = ({ password }) => {
  const { score, criteria } = validatePasswordStrength(password || '');
  const meta = getStrengthMeta(score);

  if (!password) return null;

  const requirements = [
    { key: 'length', label: 'At least 8 characters' },
    { key: 'uppercase', label: 'One uppercase letter' },
    { key: 'lowercase', label: 'One lowercase letter' },
    { key: 'number', label: 'One number' },
    { key: 'special', label: 'One special character (!@#$%^&*)' },
  ];

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 flex-1 mr-3">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                i <= meta.bars ? meta.barColor : 'bg-border'
              }`}
            />
          ))}
        </div>
        <span className="text-xs font-semibold" style={{ color: meta.hexColor }}>
          {score > 0 ? meta.label : ''}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {requirements?.map((req) => (
          <div key={req?.key} className="flex items-center gap-1.5">
            <Icon
              name={criteria?.[req?.key] ? 'CheckCircle' : 'Circle'}
              size={12}
              color={criteria?.[req?.key] ? '#22c55e' : 'var(--color-muted-foreground)'}
            />
            <span
              className="text-xs"
              style={{ color: criteria?.[req?.key] ? '#22c55e' : 'var(--color-muted-foreground)' }}
            >
              {req?.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ResetPassword = () => {
  console.log('[ResetPassword] ResetPassword rendered');
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [tokenValid, setTokenValid] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session) {
        const hash = window.location?.hash;
        if (!hash?.includes('type=recovery') && !hash?.includes('access_token')) {
          setTokenValid(false);
        }
      }
    };
    checkSession();
  }, []);

  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) {
      navigate('/sign-in', { replace: true });
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [success, countdown, navigate]);

  const validate = () => {
    console.log('[ResetPassword] validate() called');
    const errs = {};
    if (!newPassword) {
      errs.newPassword = 'New password is required';
    } else {
      console.log('[ResetPassword] About to call validatePasswordStrength() — password length:', newPassword?.length);
      try {
        const { isValid, feedback } = validatePasswordStrength(newPassword);
        console.log('[ResetPassword] validatePasswordStrength() returned — isValid:', isValid, 'feedback:', feedback);
        if (!isValid) {
          errs.newPassword = feedback?.[0] || 'Password does not meet all requirements';
        } else if (isCommonPassword(newPassword)) {
          errs.newPassword = 'Password too common, choose a stronger password';
        }
      } catch (err) {
        console.error('[ResetPassword] validatePasswordStrength() THREW:', err?.name, err?.message, err?.stack);
        errs.newPassword = 'Password validation error';
      }
    }
    if (!confirmPassword) {
      errs.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    console.log('[ResetPassword] validate() returning errors:', errs);
    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    console.log('[ResetPassword] handleSubmit called');
    const errs = validate();
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    setError('');
    setErrors({});
    try {
      console.log('[ResetPassword] About to call supabase.auth.updateUser with new password');
      const { error: updateError } = await supabase?.auth?.updateUser({ password: newPassword });
      if (updateError) {
        const msg = updateError?.message?.toLowerCase() || '';
        if (msg?.includes('expired') || msg?.includes('invalid') || msg?.includes('token')) {
          setError('Reset link expired or invalid. Please request a new one.');
          setTokenValid(false);
        } else {
          setError(updateError?.message || 'Failed to update password. Please try again.');
        }
      } else {
        setSuccess(true);
      }
    } catch (err) {
      console.error('[ResetPassword] supabase.auth.updateUser THREW — name:', err?.name, '— message:', err?.message, '— stack:', err?.stack);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const { score: pwScore } = validatePasswordStrength(newPassword || '');
  const hasErrors = Object.values(errors)?.some(Boolean);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'var(--color-background)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{ background: 'rgba(0,217,255,0.1)', border: '2px solid rgba(0,217,255,0.3)' }}
          >
            <Icon name="ShieldCheck" size={28} color="var(--color-primary)" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Reset Password</h1>
          <p className="text-sm text-muted-foreground mt-1">Create a new secure password for your account</p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl border p-8"
          style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
        >
          {/* Success State */}
          {success ? (
            <div className="text-center space-y-4">
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mx-auto"
                style={{ background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)' }}
              >
                <Icon name="CheckCircle" size={32} color="#22c55e" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground mb-2">Password Updated Successfully!</h3>
                <p className="text-sm text-muted-foreground">
                  You can now sign in with your new password.
                </p>
              </div>
              <div
                className="flex items-center justify-center gap-2 p-3 rounded-lg"
                style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}
              >
                <Icon name="Clock" size={14} color="#22c55e" />
                <p className="text-sm text-green-400">
                  Redirecting to sign in in <span className="font-bold">{countdown}</span>s...
                </p>
              </div>
              <button
                onClick={() => navigate('/sign-in', { replace: true })}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all"
                style={{ background: 'var(--color-primary)', color: '#000' }}
              >
                Sign In Now
              </button>
            </div>
          ) : !tokenValid ? (
            <div className="text-center space-y-4">
              <div
                className="inline-flex items-center justify-center w-16 h-16 rounded-full mx-auto"
                style={{ background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.3)' }}
              >
                <Icon name="AlertTriangle" size={32} color="#ef4444" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground mb-2">Reset Link Expired or Invalid</h3>
                <p className="text-sm text-muted-foreground">
                  This password reset link has expired or is invalid. Please request a new one.
                </p>
              </div>
              <button
                onClick={() => navigate('/sign-in')}
                className="w-full py-2.5 rounded-lg border text-sm font-semibold transition-all hover:bg-surface"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Global Error */}
              {error && (
                <div
                  className="flex items-start gap-2 p-3 rounded-lg"
                  style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
                  role="alert"
                >
                  <Icon name="AlertCircle" size={16} color="#ef4444" className="mt-0.5 shrink-0" />
                  <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
                </div>
              )}
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Icon
                    name="Lock"
                    size={16}
                    color="var(--color-muted-foreground)"
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                  />
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e?.target?.value);
                      if (errors?.newPassword) setErrors((p) => ({ ...p, newPassword: '' }));
                    }}
                    onBlur={() => {
                      if (newPassword) {
                        const { isValid, feedback } = validatePasswordStrength(newPassword);
                        if (!isValid) {
                          setErrors((p) => ({ ...p, newPassword: feedback?.[0] || 'Password does not meet requirements' }));
                        } else if (isCommonPassword(newPassword)) {
                          setErrors((p) => ({ ...p, newPassword: 'Password too common, choose a stronger password' }));
                        }
                      }
                    }}
                    placeholder="Min. 8 characters with special char"
                    disabled={loading}
                    autoComplete="new-password"
                    aria-invalid={!!errors?.newPassword}
                    aria-describedby={errors?.newPassword ? 'new-password-error' : undefined}
                    className={`w-full bg-surface border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
                      errors?.newPassword ? 'border-red-500' : 'border-border focus:border-primary/50'
                    } disabled:opacity-60`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    <Icon name={showNew ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
                {errors?.newPassword && (
                  <p id="new-password-error" className="mt-1 text-xs flex items-center gap-1" style={{ color: '#ef4444' }} role="alert">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.newPassword}
                  </p>
                )}
                <PasswordStrengthMeter password={newPassword} />
              </div>
              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Icon
                    name="Lock"
                    size={16}
                    color="var(--color-muted-foreground)"
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                  />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e?.target?.value);
                      if (errors?.confirmPassword) setErrors((p) => ({ ...p, confirmPassword: '' }));
                    }}
                    onBlur={() => {
                      if (confirmPassword && newPassword !== confirmPassword) {
                        setErrors((p) => ({ ...p, confirmPassword: 'Passwords do not match' }));
                      }
                    }}
                    placeholder="Re-enter your password"
                    disabled={loading}
                    autoComplete="new-password"
                    aria-invalid={!!errors?.confirmPassword}
                    aria-describedby={errors?.confirmPassword ? 'confirm-password-error' : undefined}
                    className={`w-full bg-surface border rounded-lg pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors ${
                      errors?.confirmPassword ? 'border-red-500' : 'border-border focus:border-primary/50'
                    } disabled:opacity-60`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={16} />
                  </button>
                </div>
                {errors?.confirmPassword && (
                  <p id="confirm-password-error" className="mt-1 text-xs flex items-center gap-1" style={{ color: '#ef4444' }} role="alert">
                    <Icon name="AlertCircle" size={12} />
                    {errors?.confirmPassword}
                  </p>
                )}
                {confirmPassword && !errors?.confirmPassword && newPassword === confirmPassword && (
                  <p className="mt-1 text-xs flex items-center gap-1" style={{ color: '#22c55e' }}>
                    <Icon name="CheckCircle" size={12} />
                    Passwords match
                  </p>
                )}
              </div>
              {/* Submit */}
              <button
                type="submit"
                disabled={loading || hasErrors}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ background: 'var(--color-primary)', color: '#000' }}
              >
                {loading ? (
                  <><Icon name="Loader2" size={16} className="animate-spin" /> Updating Password...</>
                ) : (
                  <><Icon name="ShieldCheck" size={16} /> Update Password</>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sign-in')}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
              >
                <Icon name="ArrowLeft" size={14} /> Back to Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
